import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

import { Button, ConfirmDialog, EmptyState, ErrorState, IconButton, Money, SkeletonRows, Status } from "../../../../components/ui";
import { focusRing } from "../../../../components/ui/cn";
import { businessApi } from "../../../../services/api";
import ClockInWidget from "../../../components/erp/ClockInWidget";
import GovernmentNumbersPanel from "../components/GovernmentNumbersPanel";
import MyDeductionsPanel from "../components/MyDeductionsPanel";
import MyPayPanel from "../components/MyPayPanel";
import {
  addDays, attentionText, describeAnyRequest, describeLeave, describeOvertime, describeShift, errorText, formatClock,
  formatDay, formatMinutes, requestTypeLabel,
} from "../myHrText";
import { MY_PAYSLIPS_BASE, paymentOf, payslipPath, periodLabel } from "../payslips/payslipView";
import { WEEKDAYS, describeWeek } from "../scheduleDays";
import {
  SCHEDULED_STATE_LABEL, describeReviewItem, describeScheduledInterval, describeScheduledPeriod, employeeReviewLine, isLiveScheduled,
} from "../scheduledOvertimeText";
import { EMPLOYEE_NUMBER_LABELS } from "../statutoryView";
import { calendarLine, canCancel, dayState, decisionLine, requestState, scheduleLine, sectionPath, showsDayState } from "./selfService";

// The sections of My HR. Each reads the employee's own data through a "me" endpoint and shows the server's own
// states. Attendance is never edited here: a wrong day becomes a correction request that someone else decides.

const panel = "rounded-panel border border-default bg-surface";
const rows = "divide-y divide-subtle";
// The overview shows the first few days to fix; Attendance has them all.
const DAYS_TO_FIX_SHOWN = 3;
const quietLink = `rounded-control text-body-sm font-medium text-accent hover:underline ${focusRing}`;
// A section's own link, a full touch target on phones (links inside a sentence stay inline).
const actionLink = `${quietLink} inline-flex items-center max-md:min-h-touch`;

function Section({ title, note, action, level = 2, children }) {
  const Heading = level === 3 ? "h3" : "h2";
  return (
    <section aria-label={title}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <Heading className={level === 3 ? "text-panel-title text-foreground" : "text-section-title text-foreground"}>{title}</Heading>
          {note ? <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">{note}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/** One "me" read with its own loading, failure and retry, so one section failing never blanks the rest. */
function useMine(fetcher, deps) {
  const [state, setState] = useState({ status: "loading", data: null, message: "" });
  const load = useCallback(() => {
    fetcher()
      .then((data) => setState({ status: "ready", data, message: "" }))
      .catch((err) => setState({ status: "error", data: null, message: errorText(err, "") }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function Loadable({ state, label, failure, children }) {
  if (state.status === "loading") return <div className={panel}><SkeletonRows rows={4} columns={2} label={label} /></div>;
  if (state.status === "error") {
    return <div className={panel}><ErrorState title={failure} description={state.message || "Check your connection and try again."} onRetry={state.reload} /></div>;
  }
  return children(state.data);
}

function State({ tone, children }) {
  return <Status tone={tone} icon={tone === "warning" || tone === "danger"} className={`text-body-sm ${tone === "neutral" ? "text-secondary" : "font-medium"}`}>{children}</Status>;
}

/** Withdrawing a request that is still waiting. The server decides whether it still can be. */
function CancelRequestDialog({ request, onClose, onDone }) {
  const confirm = async () => {
    if (request.type === "attendance_correction") await businessApi.cancelAttendanceCorrection(request.id);
    else if (request.type === "leave") await businessApi.cancelMyLeave(request.id);
    else if (request.type === "overtime") await businessApi.cancelMyOvertime(request.id);
    else await businessApi.cancelScheduleRequest(request.id);
    onDone();
  };
  return (
    <ConfirmDialog
      title="Cancel this request?"
      tone="danger"
      confirmLabel="Cancel request"
      cancelLabel="Keep it"
      failureTitle="Not cancelled"
      errorFallback="The request could not be cancelled. Try again."
      onConfirm={confirm}
      onClose={onClose}
    >
      <p className="text-body text-foreground">{requestTypeLabel(request)} — {describeAnyRequest(request)}</p>
      <p>It will not be reviewed.{request.type === "leave" ? " Nothing was taken from your leave balance." : ""} You can send a new one later.</p>
    </ConfirmDialog>
  );
}

function useCancel() {
  const { refresh, notify } = useOutletContext();
  const [cancelling, setCancelling] = useState(null);
  const dialog = cancelling ? (
    <CancelRequestDialog
      request={cancelling}
      onClose={() => setCancelling(null)}
      onDone={() => { setCancelling(null); notify("Request cancelled."); refresh(); }}
    />
  ) : null;
  return { cancel: setCancelling, dialog };
}

/** A request with its own state, who asked, what was decided, and the one thing the employee can still do. */
function RequestRow({ request, heading, line, onCancel, stateOverride = null }) {
  const state = stateOverride || requestState(request);
  const decision = decisionLine(request);
  const by = request.origin === "assigned" ? `Assigned by ${request.requestedByName || "a manager"}` : "Your reason";
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-panel-title text-foreground">{heading}</p>
        <State tone={state.tone}>{state.label}</State>
      </div>
      {line ? <p className="mt-0.5 text-body-sm text-foreground">{line}</p> : null}
      {request.reason ? <p className="mt-0.5 text-body-sm text-secondary">{by}: {request.reason}</p> : null}
      {decision ? <p className="mt-0.5 text-body-sm text-secondary">{decision}</p> : null}
      {onCancel && canCancel(request) ? <Button size="sm" className="mt-2" onClick={() => onCancel(request)}>Cancel request</Button> : null}
    </li>
  );
}

/* ── Attendance day ── */

function AttendanceDay({ day, onCorrect, onRequestOvertime }) {
  const state = dayState(day);
  const record = day.record;
  const extra = record?.classification;
  const unapproved = extra && extra.rawOvertimeMinutes > extra.approvedOvertimeMinutes;
  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:gap-4">
      <p className="w-28 shrink-0 text-body-sm font-medium text-foreground">{formatDay(day.date)}</p>
      <div className="min-w-0 flex-1 text-body-sm">
        <p className="text-foreground">{scheduleLine(day)}</p>
        {calendarLine(day) ? <p className="mt-0.5 text-secondary">{calendarLine(day)}</p> : null}
        {record?.check_in ? (
          <p className="mt-0.5 text-secondary">
            In {formatClock(record.checkInLocal)} · Out {record.checkOutLocal ? formatClock(record.checkOutLocal) : "not recorded"}
            {(record.classification?.lateMinutes ?? record.late_minutes) > 0 ? ` · ${formatMinutes(record.classification?.lateMinutes ?? record.late_minutes)} late` : ""}
            {record.clock_out_missing && record.payableUntilLocal ? ` · paid until ${formatClock(record.payableUntilLocal)} for now` : ""}
          </p>
        ) : null}
        {extra?.rawOvertimeMinutes > 0 || extra?.approvedOvertimeMinutes > 0 ? (
          <p className="mt-0.5 text-meta text-secondary">
            Worked {formatMinutes(extra.rawOvertimeMinutes)} after your shift · approved overtime {formatMinutes(extra.approvedOvertimeMinutes)}
          </p>
        ) : null}
        {extra?.unverifiedOvertimeMinutes > 0 ? (
          <p className="mt-0.5 text-meta text-warning">
            {formatMinutes(extra.unverifiedOvertimeMinutes)} of approved overtime can't be paid until your clock-out time is fixed. Request a correction with the time you left.
          </p>
        ) : null}
        {day.correction && day.correction.status !== "pending" ? (
          <p className="mt-0.5 text-meta text-secondary">Last correction request: {requestState(day.correction).label}</p>
        ) : null}
        {showsDayState(day) ? <p className="mt-1"><State tone={state.tone}>{state.label}</State></p> : null}
      </div>
      {(day.canRequestCorrection && onCorrect) || (unapproved && onRequestOvertime) ? (
        <div className="flex flex-wrap gap-2 sm:justify-end">
          {day.canRequestCorrection && onCorrect ? (
            <Button size="sm" onClick={() => onCorrect(day)}>
              {day.state === "needs_attention" && day.attention !== "missing_day" ? "Fix this day" : "Request correction"}
            </Button>
          ) : null}
          {unapproved && onRequestOvertime ? <Button size="sm" onClick={() => onRequestOvertime(day)}>Request overtime</Button> : null}
        </div>
      ) : null}
    </li>
  );
}

/* ── Overview ── */

function PayNow({ latest }) {
  if (!latest) return null;
  const payment = paymentOf(latest);
  return (
    <Section title="Pay" action={<Link to={MY_PAYSLIPS_BASE} className={actionLink}>My Payslips</Link>}>
      <div className={panel}>
        <Link to={payslipPath(latest.id)} className={`flex min-h-touch items-center justify-between gap-4 px-4 py-3 hover:bg-hover ${focusRing}`}>
          <span className="min-w-0">
            <span className="block truncate text-panel-title text-foreground">{periodLabel(latest)}</span>
            <span className="block text-meta text-secondary">Latest payslip</span>
          </span>
          <span className="shrink-0 text-right">
            <span className="sr-only">Net pay </span>
            <Money value={latest.net_pay_cents} className="block text-money text-foreground" />
            <Status tone={payment.tone} className="text-meta">{payment.label}</Status>
          </span>
        </Link>
      </div>
    </Section>
  );
}

export function MyHROverview() {
  const { overview, askCorrection, refresh, reloadKey } = useOutletContext();
  const today = overview.today;
  const state = today ? dayState(today) : null;
  // The newest payslips say whether a salary is waiting to be confirmed. If this read fails, only the pay lines are
  // missing; the rest of the overview stands.
  const pay = useMine(() => businessApi.getMyPayslips({ page: 1, limit: 3 }), [reloadKey]);
  const payslips = pay.status === "ready" ? (pay.data?.rows || []) : [];
  const salary = payslips.filter((row) => paymentOf(row).key === "confirm");
  const hasAttention = overview.attention.length > 0 || salary.length > 0;
  const toFix = overview.attention.slice(0, DAYS_TO_FIX_SHOWN);
  const moreToFix = overview.attention.length - toFix.length;
  return (
    <div className="space-y-6">
      <Section title="Today" note={formatDay(overview.todayDate, { year: true })}>
        <div className={`${panel} ${rows}`}>
          <div className="p-4">
            <p className="text-panel-title text-foreground">{scheduleLine(today)}</p>
            {calendarLine(today) ? <p className="mt-0.5 text-body-sm text-secondary">{calendarLine(today)}</p> : null}
            {today && showsDayState(today) ? <p className="mt-0.5"><State tone={state.tone}>{state.label}</State></p> : null}
          </div>
          {overview.canClock ? <ClockInWidget bare onUpdated={refresh} /> : null}
        </div>
      </Section>

      <Section title="Needs you">
        {hasAttention ? (
          <ul className={`${panel} ${rows}`}>
            {salary.map((row) => (
              <li key={`salary-${row.id}`} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-body-sm">
                  <p className="font-medium text-foreground">Salary for {periodLabel(row)}</p>
                  <p className="text-secondary">{paymentOf(row).detail}</p>
                </div>
                <Button as={Link} to={payslipPath(row.id)} size="sm" variant="primary">Confirm salary received</Button>
              </li>
            ))}
            {toFix.map((day) => (
              <li key={day.date} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-body-sm">
                  <p className="font-medium text-foreground">{formatDay(day.date)}</p>
                  <p className="text-secondary">{attentionText(day.attention)}</p>
                </div>
                {day.canRequestCorrection
                  ? <Button size="sm" onClick={() => askCorrection(day)}>Fix this day</Button>
                  : <p className="text-meta text-secondary">Ask HR to check this day</p>}
              </li>
            ))}
            {moreToFix > 0 ? (
              <li>
                <Link to={sectionPath("attendance")} className={`flex min-h-touch items-center px-4 text-body-sm font-medium text-accent hover:bg-hover ${focusRing}`}>
                  {moreToFix} more day{moreToFix === 1 ? "" : "s"} to fix in Attendance
                </Link>
              </li>
            ) : null}
          </ul>
        ) : <p className={`${panel} px-4 py-3 text-body-sm text-secondary`}>Nothing to fix or confirm. Your attendance for the last two weeks is complete.</p>}
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Next 7 days" action={<Link to={sectionPath("schedule")} className={actionLink}>Schedule</Link>}>
          <ul className={`${panel} ${rows}`}>
            {overview.upcoming.map((day) => (
              <li key={day.date} className="flex items-baseline justify-between gap-3 px-4 py-2 text-body-sm">
                <span className="w-24 shrink-0 font-medium text-foreground">{formatDay(day.date)}</span>
                <span className="min-w-0 text-right text-secondary">
                  {scheduleLine(day)}{day.source === "override" ? " · temporary" : ""}
                  {calendarLine(day) ? <span className="block text-meta">{calendarLine(day)}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <div className="space-y-6">
          <Section title="Requests" action={<Link to={sectionPath("requests")} className={actionLink}>All requests</Link>}>
            {overview.recentRequests.length ? (
              <ul className={`${panel} ${rows}`}>
                {overview.recentRequests.map((request) => {
                  const requestStatus = requestState(request);
                  return (
                    <li key={`${request.type}-${request.id}`} className="flex items-baseline justify-between gap-3 px-4 py-2 text-body-sm">
                      <span className="min-w-0 truncate text-foreground">{requestTypeLabel(request)}</span>
                      <State tone={requestStatus.tone}>{requestStatus.label}</State>
                    </li>
                  );
                })}
              </ul>
            ) : <p className={`${panel} px-4 py-3 text-body-sm text-secondary`}>No requests yet.</p>}
          </Section>
          <PayNow latest={payslips[0]} />
        </div>
      </div>
    </div>
  );
}

/* ── Schedule ── */

function usualWeek(days) {
  const week = {};
  for (const day of days) {
    if (day.source === "override") continue;
    const weekday = ((new Date(`${day.date}T00:00:00Z`).getUTCDay() + 6) % 7) + 1;
    if (week[weekday] !== undefined) continue;
    if (day.kind === "work" && day.shift) week[weekday] = { id: day.shift.id, name: day.shift.name };
    else if (day.kind === "rest") week[weekday] = null;
  }
  return week;
}

export function MySchedule() {
  const { today, reloadKey, askSchedule } = useOutletContext();
  const state = useMine(() => businessApi.getMySchedule({ from: today, to: addDays(today, 20) }).then((res) => res?.schedule?.days || []), [today, reloadKey]);
  const week = useMemo(() => usualWeek(state.data || []), [state.data]);
  const known = WEEKDAYS.every(({ day }) => week[day] !== undefined);
  const summary = known
    ? describeWeek(Object.fromEntries(WEEKDAYS.map(({ day }) => [day, week[day]?.id ?? null])), (id) => Object.values(week).find((item) => item?.id === id)?.name || "Shift")
    : null;
  const ask = () => askSchedule(Object.fromEntries(WEEKDAYS.map(({ day }) => [day, week[day]?.id ?? (week[day] === null ? null : undefined)])));
  return (
    <Section
      title="Schedule"
      note={summary ? `Usual week: ${summary}` : "Your schedule for the next three weeks."}
      action={<Button variant="primary" icon={Plus} onClick={ask}>Ask for a schedule change</Button>}
    >
      <Loadable state={state} label="Loading your schedule" failure="Your schedule couldn't be loaded">
        {(days) => (days.every((day) => day.kind === "unassigned") ? (
          <div className={panel}><EmptyState title="No schedule yet" description="You can ask for a weekly schedule, or talk to your manager." /></div>
        ) : (
          <ul className={`${panel} ${rows}`}>
            {days.map((day) => (
              <li key={day.date} className="flex flex-col gap-0.5 px-4 py-2.5 text-body-sm sm:flex-row sm:items-baseline sm:gap-4">
                <span className="w-32 shrink-0 font-medium text-foreground">{formatDay(day.date)}{day.date === today ? " · today" : ""}</span>
                <span className="min-w-0 flex-1 text-foreground">
                  {day.kind === "work" && day.shift ? describeShift(day.shift) : scheduleLine(day)}
                  {day.shift?.breakMinutes ? <span className="text-secondary"> · {day.shift.breakMinutes}-min break at {formatClock(day.shift.breakStartTime)}</span> : null}
                  {calendarLine(day) ? <span className="block text-secondary">{calendarLine(day)}</span> : null}
                </span>
                {day.source === "override" ? <span className="text-meta text-secondary">Temporary change</span> : null}
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </Section>
  );
}

/* ── Attendance ── */

export function MyAttendance() {
  const { today, reloadKey, askCorrection, askOvertime } = useOutletContext();
  const [to, setTo] = useState(today);
  const from = addDays(to, -13);
  const state = useMine(() => businessApi.getMyAttendanceDays({ from, to }), [from, to, reloadKey]);
  const requestOvertime = (day) => askOvertime({
    date: day.date,
    startTime: day.schedule?.shift?.endTime ? String(day.schedule.shift.endTime).slice(0, 5) : "",
    endTime: day.record?.checkOutLocal || "",
  });
  const worked = (state.data?.days || []).reduce((sum, day) => sum + Number(day.record?.worked_minutes || 0), 0);
  return (
    <Section
      title="Attendance"
      note={`${formatDay(from)} – ${formatDay(to, { year: true })}${state.status === "ready" ? ` · ${formatMinutes(worked)} paid time` : ""}`}
      action={
        <>
          <IconButton icon={ChevronLeft} label="Earlier two weeks" variant="secondary" onClick={() => setTo(addDays(to, -14))} />
          <IconButton icon={ChevronRight} label="Later two weeks" variant="secondary" disabled={to >= today} onClick={() => setTo(addDays(to, 14) > today ? today : addDays(to, 14))} />
        </>
      }
    >
      <Loadable state={state} label="Loading your attendance" failure="Your attendance couldn't be loaded">
        {(data) => (
          <>
            <p className="mb-3 max-w-[75ch] text-body-sm text-secondary">
              Paid time counts from your scheduled start to your scheduled end. Arriving early is recorded but not paid. If a day is wrong, request a correction; someone else reviews it.
            </p>
            <ul className={`${panel} ${rows}`}>
              {[...(data?.days || [])].reverse().map((day) => <AttendanceDay key={day.date} day={day} onCorrect={askCorrection} onRequestOvertime={requestOvertime} />)}
            </ul>
          </>
        )}
      </Loadable>
    </Section>
  );
}

/* ── Leave ── */

export function MyLeave() {
  const { reloadKey, askLeave } = useOutletContext();
  const state = useMine(() => businessApi.getMyLeave(), [reloadKey]);
  const { cancel, dialog } = useCancel();
  return (
    <Loadable state={state} label="Loading your leave" failure="Your leave couldn't be loaded">
      {(data) => {
        const balances = (data.balances || []).filter((row) => row.total > 0 || row.used > 0 || row.carriedOver > 0);
        return (
          <div className="space-y-6">
            <Section
              title={`Leave balance · ${data.year}`}
              note="Days you can still take. A request uses days only when it is approved."
              action={<Button variant="primary" icon={Plus} onClick={() => askLeave(data)}>Request leave</Button>}
            >
              {balances.length === 0 ? (
                <div className={panel}>
                  <EmptyState title="No leave balance this year" description="Paid leave needs a balance, so ask HR first. Unpaid leave needs none." />
                </div>
              ) : (
                <ul className={`${panel} ${rows}`}>
                  {balances.map((row) => (
                    <li key={row.leaveTypeId} className="flex items-baseline justify-between gap-4 px-4 py-3">
                      <span className="min-w-0">
                        <span className="block text-panel-title text-foreground">{row.name}</span>
                        <span className="block text-meta text-secondary">
                          {row.used} used of {row.total + row.carriedOver}{row.carriedOver ? ` (incl. ${row.carriedOver} carried over)` : ""}
                        </span>
                      </span>
                      <span className="shrink-0 text-money tabular-nums text-foreground">{row.available} day{row.available === 1 ? "" : "s"} left</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Leave requests">
              {(data.requests || []).length === 0 ? <p className={`${panel} px-4 py-3 text-body-sm text-secondary`}>No leave requests yet.</p> : (
                <ul className={`${panel} ${rows}`}>
                  {data.requests.map((request) => (
                    <RequestRow key={request.id} request={{ ...request, type: "leave" }} heading={request.leave_type_name} line={describeLeave(request)} onCancel={cancel} />
                  ))}
                </ul>
              )}
            </Section>
            {dialog}
          </div>
        );
      }}
    </Loadable>
  );
}

/* ── Overtime ── */

// Report 70: overtime your employer scheduled in advance (hours only), and your overtime days HR is reviewing.
function MyScheduledOvertime({ reloadKey }) {
  const state = useMine(() => Promise.all([
    businessApi.getMyScheduledOvertime().then((res) => res?.authorizations || []),
    businessApi.getMyOvertimeReviewItems().then((res) => res?.items || []).catch(() => []),
  ]), [reloadKey]);
  if (state.status !== "ready") return null;
  const [scheduled, reviews] = state.data;
  const current = scheduled.filter((item) => item.status === "pending" || isLiveScheduled(item));
  if (!current.length && !reviews.length) return null;
  return (
    <div className="space-y-6">
      {current.length ? (
        <Section level={3} title="Scheduled overtime" note="Authorised in advance on your regular days. Only the time you actually work inside it is paid.">
          <ul className={`${panel} ${rows}`}>
            {current.map((item) => (
              <li key={item.id} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 text-body-sm">
                  <p className="font-medium text-foreground">{describeScheduledInterval(item)}</p>
                  <p className="text-secondary">{describeScheduledPeriod(item)}{item.exceptions?.length ? ` · not on ${item.exceptions.map((entry) => formatDay(entry.date)).join(", ")}` : ""}</p>
                  <p className="text-meta text-tertiary">{item.reason}</p>
                </div>
                <p className="shrink-0 text-body-sm text-secondary">{SCHEDULED_STATE_LABEL[item.state] || item.state}</p>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {reviews.length ? (
        <Section level={3} title="Overtime HR is checking" note="Time that is not paid yet while HR checks it. Your clock times are kept.">
          <ul className={`${panel} ${rows}`}>
            {reviews.map((item) => (
              <li key={`${item.attendanceRecordId}-${item.kind}`} className="px-4 py-3 text-body-sm">
                <p className="text-foreground">{describeReviewItem(item)}</p>
                <p className="text-meta text-secondary">{employeeReviewLine(item)}</p>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}

// Report 70 hardening (P1): approval authorises overtime; it is not proof it was worked or paid. An approved day says how
// it is paid, and a day HR is still checking says so instead of looking settled.
function approvedOvertimeLine(item, today, reviews) {
  const checking = reviews.find((review) => review.date === item.date && review.state !== "reviewed");
  if (checking) return "Not paid yet: HR is checking this day (see Overtime HR is checking).";
  return item.date < today
    ? "Authorised. Paid for the time you actually worked inside it, in the payroll for that day."
    : "Authorised. You are paid for the time you actually work inside it.";
}

export function MyOvertime() {
  const { today, reloadKey, askOvertime } = useOutletContext();
  const state = useMine(() => Promise.all([
    businessApi.getMyOvertime().then((res) => res?.overtime || []),
    businessApi.getMyOvertimeReviewItems().then((res) => res?.items || []).catch(() => []),
  ]).then(([overtime, reviews]) => ({ overtime, reviews })), [reloadKey]);
  const { cancel, dialog } = useCancel();
  const list = (items, reviews, empty) => (items.length === 0 ? <p className="text-body-sm text-secondary">{empty}</p> : (
    <ul className={`${panel} ${rows}`}>
      {items.map((item) => {
        const approved = String(item.status || "").toLowerCase() === "approved";
        return (
          <RequestRow
            key={item.id}
            request={{ ...item, type: "overtime" }}
            heading={describeOvertime(item)}
            line={approved ? approvedOvertimeLine(item, today, reviews) : null}
            stateOverride={approved ? { tone: "neutral", label: "Approved" } : null}
            onCancel={cancel}
          />
        );
      })}
    </ul>
  ));
  return (
    <div className="space-y-6">
      <Section
        title="Overtime"
        note="Approved overtime is paid for the time you actually work inside it. Extra time you work without approval is still recorded, and HR checks it before payroll. Ask before you work overtime, or afterwards for up to 14 days. That limit is this app's rule for requests, not a cut-off on pay for time you worked: for older days, ask HR to check them."
        action={<Button variant="primary" icon={Plus} onClick={() => askOvertime({})}>Request overtime</Button>}
      >
        <Loadable state={state} label="Loading your overtime" failure="Your overtime couldn't be loaded">
          {({ overtime: items, reviews }) => (
            <div className="space-y-6">
              <MyScheduledOvertime reloadKey={reloadKey} />
              <Section level={3} title="One-day overtime: upcoming and today" note="Single days you asked for or a manager assigned. Scheduled overtime is listed above.">{list(items.filter((item) => item.date >= today), reviews, "No one-day overtime coming up.")}</Section>
              <Section level={3} title="One-day overtime: past">{list(items.filter((item) => item.date < today), reviews, "No past one-day overtime.")}</Section>
            </div>
          )}
        </Loadable>
      </Section>
      {dialog}
    </div>
  );
}

/* ── Requests ── */

export function MyRequests() {
  const { reloadKey, askSchedule } = useOutletContext();
  const state = useMine(() => businessApi.getMyRequests(), [reloadKey]);
  const { cancel, dialog } = useCancel();
  return (
    <Section
      title="Requests"
      note="Leave, overtime, attendance corrections and schedule changes you asked for, and what was decided."
      action={<Button icon={Plus} onClick={() => askSchedule(null)}>Ask for a schedule change</Button>}
    >
      <p className="mb-3 text-body-sm text-secondary">
        To correct a day, pick it in <Link to={sectionPath("attendance")} className={quietLink}>Attendance</Link>. Ask for leave in{" "}
        <Link to={sectionPath("leave")} className={quietLink}>Leave</Link> and overtime in <Link to={sectionPath("overtime")} className={quietLink}>Overtime</Link>.
      </p>
      <Loadable state={state} label="Loading your requests" failure="Your requests couldn't be loaded">
        {(data) => ((data?.requests || []).length === 0 ? (
          <div className={panel}><EmptyState title="No requests yet" description="Requests you send appear here with their decision." /></div>
        ) : (
          <ul className={`${panel} ${rows}`}>
            {data.requests.map((request) => (
              <RequestRow key={`${request.type}-${request.id}`} request={request} heading={requestTypeLabel(request)} line={describeAnyRequest(request)} onCancel={cancel} />
            ))}
          </ul>
        ))}
      </Loadable>
      {dialog}
    </Section>
  );
}

/* ── Pay ── */

// The server resolves the employee from the session; no member id is sent. Employees cannot edit these here.
const MY_GOVERNMENT_NUMBERS = {
  load: () => businessApi.getMyGovernmentNumbers(),
  reveal: (agency) => businessApi.revealMyGovernmentNumber(agency),
};

export function MyPay() {
  const { reloadKey } = useOutletContext();
  return (
    <div className="space-y-6">
      <MyPayPanel reloadKey={reloadKey} />
      <MyDeductionsPanel reloadKey={reloadKey} />
      <GovernmentNumbersPanel
        adapter={MY_GOVERNMENT_NUMBERS}
        labels={EMPLOYEE_NUMBER_LABELS}
        title="My government IDs"
        subtitle="The SSS, PhilHealth, Pag-IBIG and tax numbers your employer has on file for you."
        privacyNote="Your numbers are hidden until you choose to show one. To correct or add a number, ask HR."
        reloadKey={reloadKey}
      />
    </div>
  );
}
